import type { Booking } from "@common/types";

export type BookingSort = "status" | "date";

export const STATUS_FILTERS = ["active", "all", "pending", "confirmed", "toInvoice", "invoiced", "completed", "cancelled"] as const;
export type StatusFilterValue = (typeof STATUS_FILTERS)[number];

/** The list's filter, search, sort and page live in the address, so reload, back and shared links keep the place. */
export function readListState(params: URLSearchParams): { filter: StatusFilterValue; query: string; sort: BookingSort; page: number } {
  const filter = params.get("filter");
  return {
    filter: (STATUS_FILTERS as readonly string[]).includes(filter ?? "") ? (filter as StatusFilterValue) : "active",
    query: params.get("q") ?? "",
    sort: params.get("sort") === "date" ? "date" : "status",
    page: Math.max(1, Math.floor(Number(params.get("page"))) || 1),
  };
}

/** A new address for the list: values equal to the default are left out, and changing a filter returns to page 1. */
export function writeListState(params: URLSearchParams, patch: Partial<{ filter: StatusFilterValue; query: string; sort: BookingSort; page: number }>): URLSearchParams {
  const next = new URLSearchParams(params);
  const set = (key: string, value: string | null) => (value === null || value === "" ? next.delete(key) : next.set(key, value));
  if (patch.filter !== undefined) set("filter", patch.filter === "active" ? null : patch.filter);
  if (patch.query !== undefined) set("q", patch.query);
  if (patch.sort !== undefined) set("sort", patch.sort === "status" ? null : patch.sort);
  if (patch.page !== undefined) set("page", patch.page <= 1 ? null : String(patch.page));
  else set("page", null);
  return next;
}

const day = (b: Booking) => new Date(b.date).getTime();

/** "date": simply by trip date, soonest first (a new copy, the input is not changed). */
export function sortByDate(bookings: Booking[]): Booking[] {
  return [...bookings].sort((a, b) => day(a) - day(b) || String(a.timeSlot ?? "").localeCompare(String(b.timeSlot ?? "")));
}
